import React from "react";
import { Link } from "react-router-dom";
import { Download, Copy, ArrowLeft, Mail, Check } from "lucide-react";

const WORDMARKS = [
  { name: "Cream on Dark", file: "naami-wordmark-cream-on-dark.png", bg: "var(--bg)" },
  { name: "Gold on Dark", file: "naami-wordmark-gold-on-dark.png", bg: "var(--bg)" },
  { name: "Black on Cream", file: "naami-wordmark-black-on-cream.png", bg: "#F5F0E6" },
  { name: "Black on White", file: "naami-wordmark-black-on-white.png", bg: "#FFFFFF" },
  { name: "Cream · Transparent", file: "naami-wordmark-cream-transparent.png", bg: "var(--surface)", transparent: true },
  { name: "Gold · Transparent", file: "naami-wordmark-gold-transparent.png", bg: "var(--surface)", transparent: true },
  { name: "Black · Transparent", file: "naami-wordmark-black-transparent.png", bg: "#F5F0E6", transparent: true },
];

const MARKS = [
  { name: "Square · Dark", file: "naami-mark-square-dark.png", bg: "var(--bg)" },
  { name: "Square · Light", file: "naami-mark-square-light.png", bg: "#F5F0E6" },
];

const PALETTE = [
  { name: "Gold", hex: "#C8A97E", on: "#0C0C0C" },
  { name: "Cream", hex: "#F5F0E6", on: "#0C0C0C" },
  { name: "Naami Dark", hex: "#0C0C0C", on: "#F5F0E6" },
  { name: "Black", hex: "#000000", on: "#F5F0E6" },
];

const BOILERPLATE = `Naami Kitchens is Scotland's largest purpose-built delivery kitchen — 20 independent food brands operating under one roof at 1 Saltire Square, Edinburgh EH5 1PR. Diners can walk in and order at the counter, or get any of the 20 brands delivered via Just Eat, Deliveroo, Uber Eats and Panda. Brands span Indian, Detroit-style pizza, peri peri chicken, ramen, shawarma, parathas, desserts, and more.`;

function CopyButton({ text, label = "Copy", testId }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  };
  return (
    <button
      onClick={copy}
      className="plat-btn"
      style={{ background: "var(--surface-elevated)", border: "1px solid var(--border)", color: "var(--text)", padding: "8px 12px", fontSize: "10.5px", flex: "0 0 auto" }}
      data-testid={testId}
    >
      {copied ? <Check size={12} style={{ marginRight: 4 }} /> : <Copy size={12} style={{ marginRight: 4 }} />}
      {copied ? "Copied" : label}
    </button>
  );
}

function AssetCard({ name, file, bg, transparent }) {
  return (
    <div
      className="border border-[var(--border)] rounded-xl overflow-hidden flex flex-col hover:border-[var(--gold)]/50 transition-colors"
      data-testid={`press-asset-${file}`}
    >
      <div
        className="aspect-[16/9] flex items-center justify-center p-6 relative"
        style={{
          background: bg,
          backgroundImage: transparent
            ? "repeating-conic-gradient(rgba(255,255,255,0.04) 0deg 90deg, transparent 90deg 180deg)"
            : undefined,
          backgroundSize: transparent ? "24px 24px" : undefined,
        }}
      >
        <img
          src={`/press/${file}`}
          alt={name}
          className="max-w-full max-h-full object-contain"
          loading="lazy"
        />
      </div>
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[var(--surface)]">
        <div className="min-w-0">
          <p className="font-display text-[16px] truncate">{name}</p>
          <p className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-muted truncate">
            PNG · {transparent ? "Transparent" : "Solid BG"}
          </p>
        </div>
        <a
          href={`/press/${file}`}
          download
          className="plat-btn"
          style={{ background: "var(--gold)", color: "var(--bg)", padding: "8px 12px", fontSize: "10.5px", flex: "0 0 auto" }}
          data-testid={`press-download-${file}`}
        >
          <Download size={12} style={{ marginRight: 4 }} />
          Download
        </a>
      </div>
    </div>
  );
}

export default function PressKit() {
  return (
    <main className="bg-[var(--bg)] text-[var(--text)] min-h-screen" data-testid="press-kit-page">
      {/* Top bar */}
      <header className="border-b border-[var(--border)] sticky top-0 bg-[var(--bg)]/90 backdrop-blur-md z-10">
        <div className="container-nk py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-muted hover:text-gold font-mono text-[11px] uppercase tracking-[0.18em] transition-colors" data-testid="press-back-home">
            <ArrowLeft size={14} />
            Back to Naami
          </Link>
          <a
            href="mailto:press@naamikitchens.com"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-gold hover:gap-3 transition-all"
            data-testid="press-email-link"
          >
            <Mail size={13} />
            press@naamikitchens.com
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="container-nk pt-20 pb-14 lg:pt-28 lg:pb-20">
        <p className="eyebrow text-gold">// Press &amp; Media Kit</p>
        <h1 className="font-display mt-4 leading-[0.95]" style={{ fontSize: "clamp(56px, 9vw, 110px)" }} data-testid="press-headline">
          The <span className="text-gold">Naami</span> Press Kit.
        </h1>
        <p className="mt-6 max-w-2xl text-[15.5px] leading-relaxed text-[var(--text)]/80 font-light">
          Everything you need to write about Naami Kitchens — high-resolution
          logos, brand colours, boilerplate copy, and key facts. Hit download,
          paste and publish.
        </p>
      </section>

      {/* Key Facts */}
      <section className="container-nk pb-16">
        <div className="grid md:grid-cols-4 gap-px bg-[var(--border)] rounded-xl overflow-hidden border border-[var(--border)]">
          {[
            ["20", "Independent food brands"],
            ["1 roof", "1 Saltire Square, Edinburgh"],
            ["4+", "Delivery platforms"],
            ["7 days", "11:00 – 23:00, open daily"],
          ].map(([big, sub], i) => (
            <div key={i} className="bg-[var(--surface)] p-6">
              <p className="font-display text-[36px] text-gold leading-none">{big}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mt-2">{sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Wordmarks */}
      <section className="container-nk pb-16">
        <div className="flex items-baseline justify-between mb-7 flex-wrap gap-3">
          <h2 className="font-display text-[40px] md:text-[52px] leading-none">Wordmarks</h2>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
            7 variants · PNG · 1600px wide
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {WORDMARKS.map((a) => <AssetCard key={a.file} {...a} />)}
        </div>
      </section>

      {/* Square mark */}
      <section className="container-nk pb-16">
        <h2 className="font-display text-[40px] md:text-[52px] leading-none mb-7">The Mark</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {MARKS.map((a) => <AssetCard key={a.file} {...a} />)}
        </div>
      </section>

      {/* Press emblem */}
      <section className="container-nk pb-16">
        <h2 className="font-display text-[40px] md:text-[52px] leading-none mb-3">"As Featured" Emblem</h2>
        <p className="text-muted text-[14px] leading-relaxed max-w-2xl mb-7 font-light">
          For publications covering Naami — use this stamp on your article
          page or social media post.
        </p>
        <AssetCard name="Press Emblem" file="naami-press-emblem.png" bg="var(--surface)" transparent />
      </section>

      {/* Palette */}
      <section className="container-nk pb-16">
        <h2 className="font-display text-[40px] md:text-[52px] leading-none mb-7">Colour Palette</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {PALETTE.map((c) => (
            <div key={c.hex} className="border border-[var(--border)] rounded-xl overflow-hidden" data-testid={`press-palette-${c.name.replace(/\s+/g, '-')}`}>
              <div className="h-32" style={{ background: c.hex }} />
              <div className="bg-[var(--surface)] p-4 flex items-center justify-between gap-2">
                <div>
                  <p className="font-display text-[18px] leading-none">{c.name}</p>
                  <p className="font-mono text-[11px] text-gold mt-1.5 tabular-nums">{c.hex}</p>
                </div>
                <CopyButton text={c.hex} label="Hex" testId={`press-copy-${c.hex}`} />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5">
          <a
            href="/press/naami-palette.png"
            download
            className="btn-ghost inline-flex"
            data-testid="press-download-palette"
          >
            <Download size={14} style={{ marginRight: 6 }} />
            Download palette PNG
          </a>
        </div>
      </section>

      {/* Boilerplate */}
      <section className="container-nk pb-16">
        <h2 className="font-display text-[40px] md:text-[52px] leading-none mb-7">Boilerplate Copy</h2>
        <div className="border border-[var(--border)] rounded-xl bg-[var(--surface)] p-6 md:p-8">
          <p className="text-[15px] leading-[1.75] text-[var(--text)]/90 font-light" data-testid="press-boilerplate">
            {BOILERPLATE}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <CopyButton text={BOILERPLATE} label="Copy boilerplate" testId="press-copy-boilerplate" />
            <CopyButton text="Naami Kitchens · 1 Saltire Square, Edinburgh EH5 1PR" label="Copy address" testId="press-copy-address" />
            <CopyButton text="press@naamikitchens.com" label="Copy press email" testId="press-copy-email" />
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="container-nk pb-24">
        <div className="border border-[var(--border)] rounded-xl bg-[var(--surface-elevated)] p-8 md:p-12 grain relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-[300px] h-[300px] rounded-full bg-[var(--gold)] opacity-10 blur-3xl pointer-events-none" aria-hidden />
          <div className="relative z-10">
            <p className="eyebrow text-gold">// Press Enquiries</p>
            <h3 className="font-display text-[40px] md:text-[52px] leading-none mt-3">
              Need something we haven't listed?
            </h3>
            <p className="mt-5 text-muted text-[14px] max-w-md leading-relaxed">
              For interviews, samples, location shoots or anything not in this
              kit — get in touch and we'll respond within one business day.
            </p>
            <a
              href="mailto:press@naamikitchens.com?subject=Press%20Enquiry%20-%20Naami%20Kitchens"
              className="btn-primary mt-7 inline-flex"
              data-testid="press-cta-email"
            >
              <Mail size={14} style={{ marginRight: 6 }} />
              press@naamikitchens.com
            </a>
          </div>
        </div>
      </section>

      {/* Footer mini */}
      <footer className="border-t border-[var(--border)] bg-[var(--surface)]">
        <div className="container-nk py-6 flex justify-between items-center text-[11px] font-mono uppercase tracking-[0.16em] text-muted">
          <span>© {new Date().getFullYear()} Naami Kitchens · Edinburgh</span>
          <Link to="/" className="hover:text-gold transition-colors" data-testid="press-footer-home">naamikitchens.com</Link>
        </div>
      </footer>
    </main>
  );
}
